import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatRp, parseRupiah, formatRupiahInput } from "@/lib/constants";
import { Pill, Plus, Trash, LockSimple, LockOpen } from "@phosphor-icons/react";

const KIND_LABEL = {
  jasa_pemeriksaan: "Jasa Pemeriksaan / Konsultasi",
  jasa_tindakan: "Jasa Tindakan",
  lab: "Pemeriksaan Laboratorium",
  lainnya: "Komponen Biaya Lain",
};

export default function BillItemsPanel({ reservation, canEdit, canManualPrice, isSuperAdmin, onChanged }) {
  const rid = reservation?.id;
  const [bill, setBill] = useState(null);
  const [tindakan, setTindakan] = useState([]);
  const [tindakanId, setTindakanId] = useState("");
  const [form, setForm] = useState({ kind: "jasa_pemeriksaan", nama: "", qty: 1, harga_satuan: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!rid) return;
    api.get(`/reservations/${rid}/bill`).then((r) => setBill(r.data)).catch(() => setBill(null));
  }, [rid]);

  useEffect(() => { load(); }, [load]);

  // Kasir Shift (petugas): harga WAJIB dari Master Tindakan — muat daftarnya.
  useEffect(() => {
    if (canEdit && !canManualPrice) {
      api.get("/tindakan").then((r) => setTindakan((r.data || []).filter((t) => t.aktif !== false))).catch(() => setTindakan([]));
    }
  }, [canEdit, canManualPrice]);

  if (!reservation || (reservation.pricing_mode !== "per_item" && !bill?.items?.length)) return null;

  const finalized = bill?.bill_status === "final";
  const pickTindakan = (id) => {
    setTindakanId(id);
    const t = tindakan.find((x) => x.id === id);
    if (t) setForm((f) => ({ ...f, kind: t.kind || f.kind, nama: t.nama, harga_satuan: String(t.harga || 0) }));
  };

  const addItem = async () => {
    if (!canManualPrice && !tindakanId) return toast.error("Pilih tindakan dari Master Tindakan");
    if (canManualPrice && !form.nama.trim()) return toast.error("Nama item wajib diisi");
    const harga = parseRupiah(form.harga_satuan);
    if (harga <= 0) return toast.error("Harga item harus lebih dari 0");
    setBusy(true);
    try {
      const payload = { kind: form.kind, nama: form.nama.trim(), qty: Number(form.qty || 1), harga_satuan: harga };
      if (tindakanId) payload.tindakan_id = tindakanId;
      const r = await api.post(`/reservations/${rid}/bill-items`, payload);
      setBill(r.data); setForm({ kind: form.kind, nama: "", qty: 1, harga_satuan: "" }); setTindakanId("");
      toast.success("Item ditambahkan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah item"); } finally { setBusy(false); }
  };

  const delItem = async (id) => {
    setBusy(true);
    try { const r = await api.delete(`/reservations/${rid}/bill-items/${id}`); setBill(r.data); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } finally { setBusy(false); }
  };

  const finalize = async () => {
    setBusy(true);
    try { const r = await api.post(`/reservations/${rid}/finalize-bill`); setBill(r.data); toast.success("Tagihan difinalkan"); onChanged && onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal finalisasi"); } finally { setBusy(false); }
  };

  const unfinalize = async () => {
    const reason = window.prompt("Alasan membuka finalisasi (wajib):");
    if (reason == null) return;
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    setBusy(true);
    try { const r = await api.post(`/reservations/${rid}/unfinalize-bill`, { reason: reason.trim() }); setBill(r.data); toast.success("Finalisasi dibuka"); onChanged && onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka finalisasi"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3" data-testid="bill-items-panel">
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-bold text-indigo-700"><Pill size={14} weight="duotone" /> Rincian Tagihan Pelayanan</p>
        <span data-testid="bill-status" className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${finalized ? "border-emerald-200 bg-emerald-100 text-emerald-700" : "border-indigo-200 bg-indigo-100 text-indigo-700"}`}>{finalized ? "SUDAH DIFINALKAN" : "TAGIHAN BELUM FINAL"}</span>
      </div>

      {(bill?.items || []).length === 0 ? (
        <p className="py-2 text-xs text-slate-400">Belum ada item jasa. Tambahkan jasa pemeriksaan, tindakan, atau lab.</p>
      ) : (
        <div className="space-y-1">
          {bill.items.map((it) => (
            <div key={it.id} data-testid={`bill-item-${it.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-1.5 text-xs">
              <div className="min-w-0"><span className="font-semibold text-slate-700">{it.nama}</span> <span className="text-slate-400">· {KIND_LABEL[it.kind] || it.kind} · {it.qty}×{formatRp(it.harga_satuan)}</span></div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="font-bold text-indigo-700">{formatRp(it.subtotal)}</span>
                {canEdit && !finalized && <button data-testid={`bill-item-del-${it.id}`} onClick={() => delItem(it.id)} disabled={busy} className="text-rose-500 hover:text-rose-700"><Trash size={13} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex justify-between border-t border-indigo-100 pt-2 text-xs font-bold">
        <span className="text-slate-600">Subtotal Jasa</span><span data-testid="bill-subtotal-jasa" className="text-indigo-700">{formatRp(bill?.subtotal_jasa || 0)}</span>
      </div>
      {Number(bill?.net_obat || 0) > 0 && (
        <div className="flex justify-between text-xs"><span className="text-slate-500">Subtotal Obat (Farmasi)</span><span className="text-[#DB2777]">{formatRp(bill.net_obat)}</span></div>
      )}
      <div className="flex justify-between text-sm font-extrabold"><span className="text-slate-700">Total Akhir</span><span data-testid="bill-grand-total" className="text-[#6A38C7]">{formatRp(bill?.grand_total || 0)}</span></div>

      {canEdit && !finalized && (
        <div className="mt-3 space-y-2 rounded-lg bg-white p-2">
          {!canManualPrice ? (
            <>
              <select data-testid="bill-form-tindakan" value={tindakanId} onChange={(e) => pickTindakan(e.target.value)} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs">
                <option value="">Pilih dari Master Tindakan…</option>
                {tindakan.map((t) => <option key={t.id} value={t.id}>{t.nama} — {formatRp(t.harga)}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="bill-form-qty" type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} placeholder="Qty" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                <input data-testid="bill-form-harga" value={form.harga_satuan ? formatRp(parseRupiah(form.harga_satuan)) : ""} disabled placeholder="Harga dari master" className="rounded-lg border border-slate-200 bg-slate-100 px-2 py-1.5 text-xs text-slate-500" />
              </div>
              <p className="text-[10px] text-slate-400">Nominal mengikuti Master Tindakan dan tidak dapat diubah.</p>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="bill-form-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs">
                {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input data-testid="bill-form-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Nama item" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
              <input data-testid="bill-form-qty" type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} placeholder="Qty" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
              <input data-testid="bill-form-harga" value={form.harga_satuan} onChange={(e) => setForm({ ...form, harga_satuan: formatRupiahInput(e.target.value) })} placeholder="Harga satuan" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
            </div>
          )}
          <button data-testid="bill-add-item" onClick={addItem} disabled={busy} className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-indigo-600 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"><Plus size={13} /> Tambah Item</button>
          <button data-testid="bill-finalize" onClick={finalize} disabled={busy || !(bill?.items || []).length} className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"><LockSimple size={13} weight="fill" /> Finalkan Tagihan</button>
        </div>
      )}

      {finalized && isSuperAdmin && Number(bill?.total_dibayar || 0) === 0 && (
        <button data-testid="bill-unfinalize" onClick={unfinalize} disabled={busy} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 py-1.5 text-xs font-bold text-amber-700 hover:bg-amber-100 disabled:opacity-50"><LockOpen size={13} /> Buka Finalisasi (Super Admin)</button>
      )}
    </div>
  );
}
